<script setup lang="ts" name="BasePageHeader">
import type { TimestampType } from '~/features/useLastSync';

import BackButton from '~/components/BackButton.vue';
import BaseButton from '~/components/BaseButton.vue';
import G2OBrandPattern from '~/components/G2OBrandPattern.vue';
import LastSyncTime from '~/components/LastSyncTime.vue';
import Maintenance from '~/components/Maintenance.vue';

import type { IMaintenanceArea, IPageAction, IPageBackButton } from '~/types';

const {
  title = '',
  lastUpdated,
  subTitle = '',
  actions = [],
  withPattern = false,
  subTitlePilled = false,
  titleCapitalize,
} = defineProps<{
  title?: string;
  subTitle?: string;
  spacing?: boolean;
  lastUpdated?: TimestampType;
  backButton?: IPageBackButton;
  actions?: IPageAction[];
  loading?: boolean;
  withPattern?: boolean;
  subTitlePilled?: boolean;
  areaId?: IMaintenanceArea;
  titleCapitalize?: boolean;
}>();
</script>

<template>
  <div
    class="col-span-full flex w-full flex-wrap"
    :class="{ relative: withPattern }"
  >
    <div class="pt-10">
      <div v-if="withPattern" class="absolute right-0 top-0">
        <G2OBrandPattern />
      </div>

      <BackButton
        v-if="backButton"
        v-bind="backButton"
        data-test="back-button"
        class="mb-2 max-w-max"
      />
      <slot name="heading">
        <div v-if="!backButton" class="flex flex-wrap">
          <p
            class="text-nowrap text-h3"
            data-test="page-title"
            :class="{ capitalize: titleCapitalize }"
          >
            {{ title }}
          </p>
        </div>

        <p
          v-else
          class="text-headline"
          :class="{ capitalize: titleCapitalize }"
        >
          {{ title }}
        </p>

        <p
          v-if="subTitle"
          class="mt-2"
          :class="{
            'w-max rounded bg-secondary-3 px-2 py-1': subTitlePilled,
          }"
        >
          {{ subTitle }}
        </p>
      </slot>

      <Maintenance v-if="areaId" :area-id="areaId" class="mt-6" />
    </div>
    <div class="ml-auto flex items-end justify-end gap-2 lg:gap-4">
      <slot name="timestamp">
        <LastSyncTime v-if="lastUpdated" :lastUpdated="lastUpdated" />
      </slot>
      <slot name="button">
        <BaseButton
          v-for="(action, index) in actions"
          v-bind="action"
          :key="index"
          @click="() => action.click && action.click()"
        />
      </slot>
    </div>
  </div>
</template>
